///
/// EarthBrowser
///
/// Created by Matt Giger on 10/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The sky behind the globe: Milky Way, stars, moon and sun, all driven by
/// one ephemeris evaluated from the scene clock. The sky is Earth-fixed
/// like everything else in the scene, so the celestial sphere (and the sun
/// and moon with it) rotates by sidereal time as the clock runs.
///

import { celestialToWorldVector, eclipticNorthPole, greenwichSiderealTime, illuminatedFraction, moonPosition, sunPosition, directionToEquatorial } from "@/earthbrowser/utils/astronomy"
import { registerSkyStatsProvider } from "@/earthbrowser/utils/tileStatsRegistry"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { trig } from "@stovokor/math/math"
import { type time } from "@stovokor/math/time"
import { vec3 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { MilkyWay } from "./MilkyWay"
import { Moon } from "./Moon"
import { Stars } from "./Stars"
import { Sun } from "./Sun"

// Re-evaluate the ephemeris once the clock has moved this far (julian
// days): 20 seconds, over which the moon moves ~0.003 degrees
const cEphemerisInterval = 20 / 86400

/** World-frame (Earth-fixed) sky state shared by the sky nodes */
export class SkyEphemeris {
	/** Julian date the state was evaluated at */
	public julian = 0
	/** Greenwich mean sidereal time, radians */
	public gmst = 0
	/** Unit direction from the Earth's center to the sun */
	public sunDirection = new vec3(0, 0, 1)
	/** Geocentric sun position, km */
	public sunPosition = new vec3(0, 0, 1.5e8)
	/** Geocentric moon position, km */
	public moonPosition = new vec3(0, 0, 384400)
	/** Moon spin axis (north), used to orient the lunar texture */
	public moonNorth = vec3.unitY
	/** Lit fraction of the moon's disc as seen from the Earth, 0..1 */
	public moonIlluminated = 0

	public update(julian: number): void {
		this.julian = julian
		this.gmst = greenwichSiderealTime(julian)

		const sun = sunPosition(julian)
		this.sunDirection = celestialToWorldVector(sun.direction, this.gmst)
		this.sunPosition = this.sunDirection.multiply(sun.distanceKm)

		const moon = moonPosition(julian)
		this.moonPosition = celestialToWorldVector(moon.direction, this.gmst).multiply(moon.distanceKm)
		this.moonNorth = celestialToWorldVector(eclipticNorthPole(julian), this.gmst)
		this.moonIlluminated = illuminatedFraction(sun.direction, moon.direction)
	}
}

export class Sky extends NodeBase {
	public readonly ephemeris = new SkyEphemeris()
	public readonly stars: Stars
	public readonly milkyWay: MilkyWay
	public readonly moon: Moon
	public readonly sun: Sun

	constructor(context: StovokorContext) {
		super()
		this.milkyWay = new MilkyWay(context, this.ephemeris)
		this.stars = new Stars(context, this.ephemeris)
		this.moon = new Moon(context, this.ephemeris)
		this.sun = new Sun(context, this.ephemeris)
		this.addNode([this.milkyWay, this.stars, this.moon, this.sun])

		// Dev-only window.__ebDebug.getSky()
		registerSkyStatsProvider(() => this.debugSnapshot())
	}

	/** The base star field is part of the first frame; textures stream in */
	public get renderReady(): boolean {
		return this.stars.renderReady
	}

	/**
	 * Advance the ephemeris to the scene clock. Cheap, but skipped while the
	 * clock has barely moved since the last evaluation
	 */
	public update(clockTime: time): void {
		if (Math.abs(clockTime.julian - this.ephemeris.julian) >= cEphemerisInterval) {
			this.ephemeris.update(clockTime.julian)
		}
	}

	private debugSnapshot(): unknown {
		const e = this.ephemeris
		const geo = (v: vec3) => {
			const n = v.multiply(1 / v.length)
			return { lat: Math.asin(n.y) * trig.deg, lon: Math.atan2(n.x, n.z) * trig.deg }
		}
		const equatorial = (v: vec3) => {
			// Undo the sidereal rotation to report right ascension / declination
			const n = v.multiply(1 / v.length)
			const c = Math.cos(e.gmst)
			const s = Math.sin(e.gmst)
			const { ra, dec } = directionToEquatorial(new vec3(n.x * c + n.z * s, n.y, -n.x * s + n.z * c))
			return { raHours: ra * trig.deg / 15, decDeg: dec * trig.deg }
		}
		return {
			julian: e.julian,
			gmstHours: e.gmst * trig.deg / 15,
			sun: { subsolar: geo(e.sunDirection), ...equatorial(e.sunDirection), distanceKm: e.sunPosition.length },
			moon: {
				sublunar: geo(e.moonPosition),
				...equatorial(e.moonPosition),
				distanceKm: e.moonPosition.length,
				illuminated: e.moonIlluminated
			},
			sunAppearance: this.sun.debugAppearance(),
			textures: { milkyWay: this.milkyWay.textureLoaded, moon: this.moon.textureLoaded }
		}
	}
}
